import * as THREE from 'three';

type AxisConstraint = {
  axis: THREE.Vector3;
  factor: number;
};

const LOCAL_AXES = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, 0, 1),
] as const;

const EPSILON = 1e-8;

/**
 * Approximates local-space scale factors that satisfy world-axis resize constraints.
 *
 * This is exact for orthogonal rotations (for example, 90deg turns) and provides
 * a stable least-squares approximation for arbitrary orientations.
 */
export function computeApproximateLocalScaleFactors(
  baseScale: THREE.Vector3,
  quaternion: THREE.Quaternion,
  constraints: AxisConstraint[],
): THREE.Vector3 {
  if (constraints.length === 0) {
    return new THREE.Vector3(1, 1, 1);
  }

  const orientedLocalAxes = LOCAL_AXES.map(localAxis =>
    localAxis.clone().applyQuaternion(quaternion).normalize(),
  );

  const cRows: number[][] = [];
  const dValues: number[] = [];

  for (const constraint of constraints) {
    const axis = constraint.axis.clone().normalize();
    const baseComponents = [baseScale.x, baseScale.y, baseScale.z];

    const row = orientedLocalAxes.map((localAxis, index) => {
      const projection = Math.abs(localAxis.dot(axis));
      return projection * baseComponents[index]!;
    });

    const baseline = row[0]! + row[1]! + row[2]!;
    if (baseline < EPSILON) {
      continue;
    }

    cRows.push(row);
    dValues.push((constraint.factor - 1) * baseline);
  }

  const rowCount = cRows.length;
  if (rowCount === 0) {
    return new THREE.Vector3(1, 1, 1);
  }

  const g00 = cRows[0]![0]! ** 2 + cRows[0]![1]! ** 2 + cRows[0]![2]! ** 2;
  const g01 =
    rowCount > 1
      ? cRows[0]![0]! * cRows[1]![0]! +
        cRows[0]![1]! * cRows[1]![1]! +
        cRows[0]![2]! * cRows[1]![2]!
      : 0;
  const g11 =
    rowCount > 1
      ? cRows[1]![0]! ** 2 + cRows[1]![1]! ** 2 + cRows[1]![2]! ** 2
      : 0;

  let lambda0 = 0;
  let lambda1 = 0;

  if (rowCount === 1) {
    lambda0 = dValues[0]! / Math.max(g00, EPSILON);
  } else {
    const regG00 = g00 + EPSILON;
    const regG11 = g11 + EPSILON;
    const determinant = regG00 * regG11 - g01 * g01;

    if (Math.abs(determinant) > EPSILON) {
      const inv00 = regG11 / determinant;
      const inv01 = -g01 / determinant;
      const inv11 = regG00 / determinant;

      lambda0 = inv00 * dValues[0]! + inv01 * dValues[1]!;
      lambda1 = inv01 * dValues[0]! + inv11 * dValues[1]!;
    } else {
      lambda0 = dValues[0]! / Math.max(g00, EPSILON);
      lambda1 = dValues[1]! / Math.max(g11, EPSILON);
    }
  }

  const deltaX =
    cRows[0]![0]! * lambda0 + (rowCount > 1 ? cRows[1]![0]! * lambda1 : 0);
  const deltaY =
    cRows[0]![1]! * lambda0 + (rowCount > 1 ? cRows[1]![1]! * lambda1 : 0);
  const deltaZ =
    cRows[0]![2]! * lambda0 + (rowCount > 1 ? cRows[1]![2]! * lambda1 : 0);

  return new THREE.Vector3(
    Math.max(0, 1 + deltaX),
    Math.max(0, 1 + deltaY),
    Math.max(0, 1 + deltaZ),
  );
}
